import { SourceWithSourceQuestions } from "../../../../../swaggerServices";
import { Instruction, InstructionData } from "./instruction";
import { makePlaceholderInstruction, startInstruction } from "./transient";
import { Block, BlockType } from "./block";
import { CalcInput } from "./calc-input";
import { calculationSource } from "./transient";
import { TypeSubtypeLookup } from "../../../../shared/util/tax-return-type-subtype";

const UNKNOWN_FUNCTION_BLOCK: Block = {
  id: "unknown-function-block",
  name: "Unknown Function (error)",
  description: "",
  documentation: {},
  type: BlockType.Function,
};
const UNKNOWN_DECISION_BLOCK: Block = {
  id: "unknown-decision-block",
  name: "Unknown Decision (error)",
  description: "",
  documentation: {},
  type: BlockType.Decision,
};

export const endBlock: Block = {id: "end", name: "End", description: "", documentation: {}, type: BlockType.End};

export const makeEndInstruction = id => {
  return new Instruction(id, "End", endBlock, [], undefined, undefined, 1, 1, 1);
};

export interface ProgramData {
  instructions: InstructionData[];
}

export class Program {
  static fromData(data: ProgramData, sources: Map<string, SourceWithSourceQuestions>, blocks: Map<string, Block>, addMissingEndBlocks: boolean, typeSubtypeLookup: TypeSubtypeLookup, isGlobalCalcPlan: boolean) {
    if (data.instructions.length === 0) {
      return new Program([startInstruction, makePlaceholderInstruction()]);
    }
    const instructions = Program.instructionsFromData(data.instructions, sources, blocks, typeSubtypeLookup, isGlobalCalcPlan);
    const program = new Program([startInstruction].concat(instructions));
    if (addMissingEndBlocks) {
      program.addMissingEndBlocks();
    }
    return program;
  }

  static instructionsFromData(data: InstructionData[], sources: Map<string, SourceWithSourceQuestions>, blocks: Map<string, Block>, typeSubtypeLookup: TypeSubtypeLookup, isGlobalCalcPlan: boolean) {
    return data.map(i => Program.instructionFromData(i, sources, blocks, typeSubtypeLookup, isGlobalCalcPlan));
  }

  static instructionFromData(data: InstructionData, sources: Map<string, SourceWithSourceQuestions>, blocks: Map<string, Block>, typeSubtypeLookup: TypeSubtypeLookup, isGlobalCalcPlan: boolean) {
    let block = blocks.get(data.block);
    let name = data.name;
    if (!block) {
      if (data.yes || data.no) {
        block = UNKNOWN_DECISION_BLOCK;
        name = `${name} - ${block.name}`;
      } else {
        block = UNKNOWN_FUNCTION_BLOCK;
        name = `${name} - ${block.name}`;
      }
    }
    const isTerminal = block.type === BlockType.Value || block.type === BlockType.End;
    const getComplexity = complexity => isTerminal ? complexity || 1 : undefined;
    return new Instruction(
      data.id,
      name,
      block,
      data.inputs.map(i => CalcInput.fromData(i, sources, typeSubtypeLookup, isGlobalCalcPlan)),
      data.yes ? Program.instructionsFromData(data.yes, sources, blocks, typeSubtypeLookup, isGlobalCalcPlan) : null,
      data.no ? Program.instructionsFromData(data.no, sources, blocks, typeSubtypeLookup, isGlobalCalcPlan) : null,
      getComplexity(data.complexityPreparer),
      getComplexity(data.complexityReviewer1),
      getComplexity(data.complexityReviewer2),
      data.description,
    );
  }

  static walkInstructions(instructions: Instruction[], func: (instruction: Instruction) => false | void) {
    instructions.forEach(i => {
      // func returns false to "break" out of the walk a bit earlier
      if (func(i) === false) {
        return;
      }
      if (i.yes) {
        Program.walkInstructions(i.yes, func);
      }
      if (i.no) {
        Program.walkInstructions(i.no, func);
      }
    });
  }

  constructor(public instructions: Instruction[]) {
  }

  maxId(): number {
    let maxId = 1;
    Program.walkInstructions(this.instructions, i => {
      maxId = Math.max(maxId, i.id);
    });
    return maxId;
  }

  pathToInstruction(instruction: Instruction): Instruction[] | null {
    const path: Instruction[] = [];
    const find = instructions => {
      if (instructions.length) {
        const cur = instructions[0];
        path.push(cur);
        if (cur.id === instruction.id) {
          return path;
        }
        if (cur.yes) {
          if (find(cur.yes)) {
            return path;
          }
        }
        if (cur.no) {
          if (find(cur.no)) {
            return path;
          }
        }
        if (instructions.length > 1) {
          if (find(instructions.slice(1))) {
            return path;
          }
        }
        path.pop();
      }
      return null;
    };
    return find(this.instructions);
  }

  // The given calcInput can only refer to previous calculations in the tree,
  // find it and return it. Calculations later in the tree are not resolved.
  resolveIntermediateCalcInput(instruction: Instruction, calcInput: CalcInput) {
    if (calcInput.source === calculationSource) {
      const path = this.pathToInstruction(instruction);
      if (path !== null) {
        const field = path.find(i => i.id === calcInput.field);
        return new CalcInput(calcInput.source, field != null ? field : "ref error", null, calcInput.isGlobalCalcPlan);
      }
    }
  }

  // automatically create END blocks
  addMissingEndBlocks() {
    let idAlloc = this.maxId();
    const nextId = () => {
      idAlloc += 1;
      return idAlloc;
    };

    const addMissing = instructions => {
      if (instructions.length > 0) {
        const last = instructions[instructions.length - 1];
        if (last.block.type !== BlockType.Value && last.block.type !== BlockType.End && last.block.type !== BlockType.Decision) {
          instructions.push(makeEndInstruction(nextId()));
        }
      } else {
        instructions.push(makeEndInstruction(nextId()));
      }
      instructions.forEach(i => {
        if (i.yes) {
          addMissing(i.yes);
        }
        if (i.no) {
          addMissing(i.no);
        }
      });
    };

    addMissing(this.instructions);
  }
}
